import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import classNames from '@/common/utils/classNames';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Profile sections reachable from the tabs, in page order; a tab shows only when its section is on the page. */
const TABS = [
  { id: 'book-me', label: 'نوبت' },
  { id: 'phone-and-address', label: 'آدرس و تلفن' },
  { id: 'about', label: 'درباره' },
  { id: 'reviews', label: 'نظرات' },
] as const;

/** App bar (56px) + these tabs (44px): a section counts as current once its top passes under them. */
const STICKY_OFFSET = 56 + 44;

/**
 * New app only: section tabs that stick under the app bar while the profile scrolls (Digikala product
 * page pattern) and follow the reading position. Replaces the profile head's own «نظرات / آدرس و تلفن»
 * row in the app (hidden in globals.css), which only scrolled and could not stick.
 */
export const ProfileSectionTabs = () => {
  const isApp = useIsNewAppShell();
  // null until the first DOM read: the strip is drawn at once with all tabs, so it never pops in and pushes the page down.
  const [available, setAvailable] = useState<string[] | null>(null);
  const [active, setActive] = useState<string>('');
  const lockUntil = useRef(0);

  // Sections reveal progressively, so the tab list follows the DOM.
  useEffect(() => {
    if (!isApp) return;
    let frame = 0;
    const read = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const ids = TABS.filter(tab => document.getElementById(tab.id)).map(tab => tab.id);
        setAvailable(prev => (prev && prev.join() === ids.join() ? prev : ids));
      });
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [isApp]);

  useEffect(() => {
    if (!isApp || !available?.length) return;
    const onScroll = () => {
      if (Date.now() < lockUntil.current) return;
      let current = available[0];
      for (const id of available) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - STICKY_OFFSET <= 8) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isApp, available]);

  const goTo = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    setActive(id);
    // Hold the tapped tab while the smooth scroll passes the sections before it.
    lockUntil.current = Date.now() + 800;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - STICKY_OFFSET + 1, behavior: 'smooth' });
  }, []);

  if (!isApp || (available && available.length < 2)) return null;

  return (
    <nav
      aria-label="بخش‌های پروفایل"
      className="sticky top-14 z-30 mb-3 flex h-11 w-full bg-white shadow-[0_1px_0_rgba(0,0,0,0.06)] lg:hidden"
    >
      {TABS.filter(tab => !available || available.includes(tab.id)).map(tab => {
        const selected = tab.id === (active || TABS[0].id);
        return (
          <button
            key={tab.id}
            type="button"
            aria-current={selected || undefined}
            onClick={() => goTo(tab.id)}
            className={classNames(
              'relative flex flex-1 items-center justify-center text-[14px] leading-5 transition-colors',
              selected ? 'font-medium text-primary' : 'text-slate-500',
            )}
          >
            {tab.label}
            <span
              aria-hidden
              className={classNames(
                'absolute bottom-0 h-[3px] w-10 rounded-t-full bg-primary transition-opacity',
                selected ? 'opacity-100' : 'opacity-0',
              )}
            />
          </button>
        );
      })}
    </nav>
  );
};

export default ProfileSectionTabs;
